import './coapplicantEditIncomePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { additionalIncomeTypeOptions } from '../../../store/applicantStore/constants/additionalIncomeTypeOptions';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantAdditionalIncomeAmount,
    coapplicantAdditionalIncomeDurationMonths,
    coapplicantAdditionalIncomeDurationYears,
    coapplicantAdditionalIncomeEmployerName,
    coapplicantAdditionalIncomeFrequency,
    coapplicantAdditionalIncomeJobTitle,
    coapplicantAdditionalIncomeType,
    hasAdditionalIncome,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantAdditionalIncomeAmount,
    setCoapplicantAdditionalIncomeDurationMonths,
    setCoapplicantAdditionalIncomeDurationYears,
    setCoapplicantAdditionalIncomeEmployerName,
    setCoapplicantAdditionalIncomeFrequency,
    setCoapplicantAdditionalIncomeJobTitle,
    setCoapplicantAdditionalIncomeType,
    setHasAdditionalIncome,
    syncCoapplicant,
  } = useCoapplicantStore();
  const {
    clearErrors,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      hasAdditionalIncomeValue: hasAdditionalIncome,
      incomeTypeValue: coapplicantAdditionalIncomeType,
      incomeFrequencyValue: coapplicantAdditionalIncomeFrequency,
      incomeAmountValue: toCurrencyString(coapplicantAdditionalIncomeAmount),
      incomeEmployerNameValue: coapplicantAdditionalIncomeEmployerName,
      incomeJobTitleValue: coapplicantAdditionalIncomeJobTitle,
      incomeDurationYearsValue: coapplicantAdditionalIncomeDurationYears,
      incomeDurationMonthsValue: coapplicantAdditionalIncomeDurationMonths,
    },
  });

  const shouldShowIncomeFields = watch('hasAdditionalIncomeValue') === YES;

  const additionalIncomeValidation = {
    required: t('validation.additionalIncome.required'),
  };

  // Register options hand the change event straight through, not the value.
  const handleAdditionalIncomeChange = (event: {
    target: { value: string };
  }): void => {
    if (event.target.value !== YES) {
      // `formSubmit` discards these once the answer is No, so blank them rather
      // than leave a stale value behind the disabled fields.
      setValue('incomeAmountValue', '');
      setValue('incomeEmployerNameValue', '');
      setValue('incomeJobTitleValue', '');

      clearErrors([
        'incomeAmountValue',
        'incomeEmployerNameValue',
        'incomeJobTitleValue',
      ]);
    }
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      hasAdditionalIncomeValue,
      incomeAmountValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeTypeValue,
    } = values;

    setHasAdditionalIncome(hasAdditionalIncomeValue);

    if (shouldShowIncomeFields) {
      setCoapplicantAdditionalIncomeAmount(incomeAmountValue);
      setCoapplicantAdditionalIncomeFrequency(incomeFrequencyValue);
      setCoapplicantAdditionalIncomeType(incomeTypeValue);

      if (showSecondaryEmploymentFields) {
        setCoapplicantAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
        setCoapplicantAdditionalIncomeDurationYears(incomeDurationYearsValue);
        setCoapplicantAdditionalIncomeEmployerName(incomeEmployerNameValue);
        setCoapplicantAdditionalIncomeJobTitle(incomeJobTitleValue);
      } else {
        setCoapplicantAdditionalIncomeDurationMonths(null);
        setCoapplicantAdditionalIncomeDurationYears(null);
        setCoapplicantAdditionalIncomeEmployerName(null);
        setCoapplicantAdditionalIncomeJobTitle(null);
      }
    } else {
      setCoapplicantAdditionalIncomeAmount(null);
      setCoapplicantAdditionalIncomeFrequency(null);
      setCoapplicantAdditionalIncomeType(null);
      setCoapplicantAdditionalIncomeDurationMonths(null);
      setCoapplicantAdditionalIncomeDurationYears(null);
      setCoapplicantAdditionalIncomeEmployerName(null);
      setCoapplicantAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-edit-income-page">
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditIncome.form.h1}>
                {t('CoapplicantEditIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditIncome.form.h2}>
                {t('CoapplicantEditIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditIncome.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('hasAdditionalIncomeValue', {
                      ...additionalIncomeValidation,
                      onChange: handleAdditionalIncomeChange,
                    })}
                    error={errors.hasAdditionalIncomeValue}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form
                        .hasAdditionalIncomeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditIncome.form
                        .hasAdditionalIncomeSelect
                    }
                    label={t('CoapplicantEditIncomePage.additionalIncome')}
                    options={additionalIncomeTypeOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('incomeTypeValue', {
                      ...incomeTypeValidation,
                      disabled: !shouldShowIncomeFields,
                    })}
                    error={errors.incomeTypeValue}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditIncome.form.incomeTypeSelect
                    }
                    label={t('CoapplicantEditIncomePage.incomeType')}
                    options={incomeTypeOptions}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('incomeEmployerNameValue', {
                        ...incomeEmployerNameValidation,
                        disabled: !shouldShowIncomeFields,
                      })}
                      data-testid={
                        locator.pages.coapplicantEditIncome.form
                          .employerNameInput
                      }
                      error={errors.incomeEmployerNameValue}
                      errorTestId={
                        locator.pages.coapplicantEditIncome.form
                          .employerNameInputError
                      }
                      label={t('CoapplicantEditIncomePage.employerName')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('incomeJobTitleValue', {
                        ...incomeJobTitleValidation,
                        disabled: !shouldShowIncomeFields,
                      })}
                      data-testid={
                        locator.pages.coapplicantEditIncome.form.jobTitleInput
                      }
                      error={errors.incomeJobTitleValue}
                      errorTestId={
                        locator.pages.coapplicantEditIncome.form
                          .jobTitleInputError
                      }
                      label={t('CoapplicantEditIncomePage.jobTitle')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        {...register('incomeDurationYearsValue', {
                          ...incomeDurationYearsValidation,
                          disabled: !shouldShowIncomeFields,
                        })}
                        error={errors.incomeDurationYearsValue}
                        errorTestId={
                          locator.pages.coapplicantEditIncome.form
                            .yearSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditIncome.form.yearSelect
                        }
                        label={t('CoapplicantEditIncomePage.durationYears')}
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        {...register('incomeDurationMonthsValue', {
                          ...incomeDurationMonthsValidation,
                          disabled: !shouldShowIncomeFields,
                        })}
                        error={errors.incomeDurationMonthsValue}
                        errorTestId={
                          locator.pages.coapplicantEditIncome.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditIncome.form.monthSelect
                        }
                        label={t('CoapplicantEditIncomePage.durationMonths')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('incomeFrequencyValue', {
                      ...incomeFrequencyValidation,
                      disabled: !shouldShowIncomeFields,
                    })}
                    error={errors.incomeFrequencyValue}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditIncome.form.frequencySelect
                    }
                    label={t('CoapplicantEditIncomePage.incomeFrequency')}
                    options={incomeFrequencyOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('incomeAmountValue', {
                      ...incomeAmountValidation,
                      disabled: !shouldShowIncomeFields,
                    })}
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.amountInput
                    }
                    error={errors.incomeAmountValue}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form.amountInputError
                    }
                    label={t('CoapplicantEditIncomePage.incomeAmount')}
                    currency
                  />
                </div>
              </div>
              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditIncomePage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingCoapplicant}
                    fullWidth
                    submit
                  >
                    {t('CoapplicantEditIncomePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditIncome.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditIncomePage';
